/**
 * draftIntentDetector — strict explicit-verb classifier.
 *
 * Returns TRUE only when the user's text is unambiguously a drafting
 * request ("صغ عقد", "draft a partnership agreement", …). Implicit
 * cues like "أحتاج عقد عمل" / "I need an NDA" are intentionally NOT
 * classified as drafting — those route through the normal Conductor
 * Q&A flow, and the user can always click the Draft button (which is
 * the unambiguous fallback) to force the drafting pipeline.
 *
 * Design rationale (May-2026 routing fix):
 *   • False positives are far more damaging than false negatives.
 *     Routing a legal-Q&A request into the drafting pipeline produces
 *     a multi-minute background job for what should have been a
 *     30-second answer.
 *   • The Draft quick-action button is always the unambiguous override.
 *
 * Pure function, no I/O, no side effects — directly unit-testable.
 */

// Arabic explicit drafting verbs (full-word or with common prefixes).
// Each entry is a single verb stem; we match it as an explicit token so
// substrings inside unrelated words don't false-positive.
const AR_EXPLICIT_VERBS = [
  'صغ',         // imperative of صياغة (draft, formulate)
  'اصِغ',       // alternative imperative (with kasra)
  'اصغ',        // imperative without harakat
  'صياغة',      // noun-form ("drafting")
  'صياغه',      // common typo (no taa-marbuta)
  'حرر',        // "write/compose"
  'حرّر',       // with shadda
  'اكتب',       // "write" — only when paired with "عقد" / "اتفاقية" / "مذكرة"
  'أعد صياغة',  // "re-draft"
  'اعد صياغة',  // without hamza
];

// AR objects that, combined with اكتب, mark drafting intent.
// "اكتب رسالة" → not drafting; "اكتب عقد" → drafting.
const AR_DRAFTING_OBJECTS = [
  'عقد',          // contract
  'اتفاقية',      // agreement
  'مذكرة',        // memorandum (MoU)
  'وثيقة قانونية', // "legal document"
  'بنود',         // clauses
  'مسودة',        // draft
];

// EN explicit drafting patterns. Anchored to start with the verb to
// avoid false-positives on noun forms ("drafting style", "the contract
// was drafted by"). After the verb, allow an optional article + an
// optional single modifier word (e.g. "partnership", "service") before
// the drafting object so "draft a partnership agreement" matches but
// "draft style guides about agreements" still misses (the modifier
// window is one word, not arbitrary text).
const EN_PATTERNS = [
  /\bdraft\s+(?:a|an|the|me\s+a|me\s+an)?\s*(?:\w+\s+)?(?:contract|agreement|nda|mou|memorandum|document|clause|letter|deed|policy)\b/i,
  /\bwrite\s+(?:me\s+)?(?:a|an|the)\s*(?:\w+\s+)?(?:contract|agreement|nda|mou|memorandum)\b/i,
  /\bgenerate\s+(?:a|an|the)?\s*(?:\w+\s+)?(?:contract|agreement|nda|mou|memorandum|clause)\b/i,
  /\bprepare\s+(?:a|an|the)?\s*(?:\w+\s+)?(?:contract|agreement|nda|mou)\b/i,
  /\bcreate\s+(?:a|an|the)?\s*(?:\w+\s+)?(?:contract|agreement|nda|mou)\b/i,
  /\bredraft\s+(?:a|an|the|this|that|my)?\s*(?:\w+\s+)?(?:contract|agreement|document|nda|mou)\b/i,
  /\bre-draft\s+(?:a|an|the|this|that|my)?\s*(?:\w+\s+)?(?:contract|agreement|document|nda|mou)\b/i,
];

// Mixed-language: English verb head followed by Arabic drafting object.
// Catches "draft an عقد عمل" / "write me a اتفاقية شراكة".
const EN_VERB_WITH_AR_OBJECT = /\b(?:draft|write|generate|prepare|create|redraft|re-draft)\b[^\n]{0,20}(?:عقد|اتفاقية|مذكرة|مسودة)/i;

/**
 * @param {string} text - The user's raw message text.
 * @param {string} [language] - 'ar' | 'en' (unused for detection; kept for symmetry with backend).
 * @returns {boolean} True iff the text is an explicit drafting verb-phrase.
 */
export function isDraftingIntent(text, language) {  // eslint-disable-line no-unused-vars
  if (typeof text !== 'string') return false;
  const trimmed = text.trim();
  if (!trimmed) return false;

  // English patterns — anchored regexes, low false-positive rate.
  for (const re of EN_PATTERNS) {
    if (re.test(trimmed)) return true;
  }
  // Mixed-language: EN verb + AR object (e.g. "Draft an عقد").
  if (EN_VERB_WITH_AR_OBJECT.test(trimmed)) return true;

  // Arabic — strict explicit-verb match. We require either:
  //   (a) a standalone explicit verb (صياغة, حرر, صغ, …) AT THE START of
  //       the text (whitespace-tolerant), OR
  //   (b) the verb appears AND is followed (within ~30 chars) by one of
  //       the drafting object nouns. This catches "صياغة اتفاقية سرية"
  //       and "صغ لي عقد عمل" without catching "أحتاج عقد عمل" (no verb).
  const lower = trimmed; // Arabic is case-less; keep as-is.
  for (const verb of AR_EXPLICIT_VERBS) {
    // (a) verb at start, bounded by space or end of string
    const startRe = new RegExp('^\\s*' + escapeRe(verb) + '(?:\\s|$|[ًٌٍَُِّْ])');
    if (startRe.test(lower)) {
      // For اكتب specifically, require a drafting object somewhere downstream.
      if (verb === 'اكتب' || verb === 'حرر' || verb === 'حرّر') {
        if (!AR_DRAFTING_OBJECTS.some((obj) => lower.includes(obj))) continue;
      }
      return true;
    }
    // (b) verb anywhere + object within ~30 chars on the right
    const idx = lower.indexOf(verb);
    if (idx === -1) continue;
    const window = lower.slice(idx, idx + verb.length + 30);
    if (AR_DRAFTING_OBJECTS.some((obj) => window.includes(obj))) {
      return true;
    }
  }

  return false;
}

function escapeRe(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export default isDraftingIntent;
